import { Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { portalAuthGuard } from './core/guards/portal-auth.guard';
import { portalGuestGuard } from './core/guards/portal-guest.guard';
import { PrivateLayoutComponent } from './layout/private-layout/private-layout.component';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'login'
  },
  {
    path: 'login',
    title: 'Entrar',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/login/login.component').then((m) => m.LoginComponent)
  },
  {
    path: 'forgot-password',
    title: 'Recuperar senha',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent)
  },
  {
    path: 'auth/reset',
    title: 'Definir senha',
    loadComponent: () =>
      import('./features/auth/password-redeem/password-redeem.component').then((m) => m.PasswordRedeemComponent)
  },
  {
    path: 'auth/redeem',
    title: 'Definir senha',
    loadComponent: () =>
      import('./features/auth/password-redeem/password-redeem.component').then((m) => m.PasswordRedeemComponent)
  },
  {
    // Rota unificada: sem ?token= renderiza o login normal; com ?token=
    // renderiza o fluxo de convite/reset (link enviado por e-mail aponta
    // para /cliente/login?token=XXXX).
    path: 'cliente/login',
    title: 'Portal do cliente',
    canActivate: [portalGuestGuard],
    loadComponent: () => import('./features/portal/portal-login/portal-login-entry.component').then((m) => m.PortalLoginEntryComponent)
  },
  {
    path: 'cliente/forgot-password',
    title: 'Recuperar senha · Portal do cliente',
    canActivate: [portalGuestGuard],
    loadComponent: () =>
      import('./features/portal/portal-forgot-password/portal-forgot-password.component').then(
        (m) => m.PortalForgotPasswordComponent
      )
  },
  {
    path: 'cliente/redeem-token',
    pathMatch: 'full',
    redirectTo: 'cliente/login'
  },
  {
    path: 'cliente/change-password',
    pathMatch: 'full',
    redirectTo: 'cliente/account/security'
  },
  {
    path: 'cliente/home',
    pathMatch: 'full',
    redirectTo: 'cliente'
  },
  // Integração do Google Agenda no portal saiu da tela (será refeita de outro jeito).
  {
    path: 'cliente/account/integrations/google-calendar',
    pathMatch: 'full',
    redirectTo: 'cliente/account'
  },
  // Portal do cliente: moldura única (topo + barra inferior) para todas as páginas internas.
  {
    path: 'cliente',
    canActivate: [portalAuthGuard],
    canActivateChild: [portalAuthGuard],
    loadComponent: () => import('./features/portal/layout/portal-layout.component').then((m) => m.PortalLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Início',
        loadComponent: () => import('./features/portal/portal-home/portal-home.component').then((m) => m.PortalHomeComponent)
      },
      {
        path: 'cases',
        title: 'Meus processos',
        loadComponent: () => import('./features/portal/portal-legal-case-list/portal-legal-case-list.component').then((m) => m.PortalLegalCaseListComponent)
      },
      {
        path: 'cases/:publicId',
        title: 'Processo',
        loadComponent: () => import('./features/portal/portal-legal-case-detail/portal-legal-case-detail.component').then((m) => m.PortalLegalCaseDetailComponent)
      },
      {
        path: 'payments',
        title: 'Pagamentos',
        loadComponent: () => import('./features/portal/portal-payments/portal-payments.component').then((m) => m.PortalPaymentsComponent)
      },
      // Link antigo "Minhas Cobranças" (develop) aponta para a tela de Pagamentos.
      { path: 'billing', pathMatch: 'full', redirectTo: 'payments' },
      {
        path: 'documents',
        title: 'Documentos',
        loadComponent: () => import('./features/portal/portal-documents/portal-documents.component').then((m) => m.PortalDocumentsComponent)
      },
      {
        path: 'account',
        title: 'Minha conta',
        loadComponent: () => import('./features/portal/portal-account/portal-account.component').then((m) => m.PortalAccountComponent)
      },
      {
        path: 'account/security',
        title: 'Senha',
        loadComponent: () => import('./features/portal/portal-change-password/portal-change-password.component').then((m) => m.PortalChangePasswordComponent)
      }
    ]
  },
  // Compatibilidade com links de convite/reset já enviados por e-mail antes
  // do rename de /portal para /cliente (o backend não aceita mais /portal).
  {
    path: 'portal/auth/redeem',
    pathMatch: 'full',
    redirectTo: 'cliente/login'
  },
  {
    path: 'portal/redeem-token',
    pathMatch: 'full',
    redirectTo: 'cliente/login'
  },
  {
    path: 'app',
    component: PrivateLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard'
      },
      // Página de acesso negado — não precisa de permissão
      {
        path: 'access-denied',
        loadComponent: () => import('./features/auth/access-denied/access-denied.component').then((m) => m.AccessDeniedComponent)
      },

      // Dashboard
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        data: { permission: 'dashboard.view', title: 'Painel' },
        loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent)
      },

      // Publicações do DJEN
      {
        path: 'publications',
        canActivate: [permissionGuard],
        data: { permission: 'publications.view', title: 'Publicações' },
        loadComponent: () => import('./features/publications/publication-list.component').then((m) => m.PublicationListComponent)
      },
      // Partes (menores representados, parte contrária, terceiros)
      {
        path: 'people',
        canActivate: [permissionGuard],
        data: { permission: 'people.view', title: 'Partes' },
        loadComponent: () => import('./features/people/person-list.component').then((m) => m.PersonListComponent)
      },
      {
        path: 'account/security',
        data: { title: 'Segurança da conta' },
        loadComponent: () =>
          import('./features/account/account-security/account-security.component').then((m) => m.AccountSecurityComponent)
      },

       // Usuários internos
       {
         path: 'users',
         canActivate: [permissionGuard],
         data: { permission: 'users.view', title: 'Usuários' },
         loadComponent: () => import('./features/users/user-list/user-list.component').then((m) => m.UserListComponent)
       },
       {
         path: 'users/new',
         canActivate: [permissionGuard],
         data: { permission: 'users.create', title: 'Novo usuário' },
         loadComponent: () => import('./features/users/user-form/user-form.component').then((m) => m.UserFormComponent)
       },
       {
         path: 'users/:publicId/edit',
         canActivate: [permissionGuard],
         data: { permission: 'users.update', title: 'Editar usuário' },
         loadComponent: () => import('./features/users/user-form/user-form.component').then((m) => m.UserFormComponent)
       },
       {
         path: 'users/:publicId/access',
         canActivate: [permissionGuard],
         data: { permission: 'users.permissions.manage', title: 'Permissões do usuário' },
         loadComponent: () => import('./features/users/user-access/user-access.component').then((m) => m.UserAccessComponent)
       },

       // Perfis e Permissões
       {
         path: 'roles',
         canActivate: [permissionGuard],
         data: { permission: 'roles.view', title: 'Perfis de acesso' },
         loadComponent: () => import('./features/roles/role-list/role-list.component').then((m) => m.RoleListComponent)
       },
       {
         path: 'roles/:publicId/permissions',
         canActivate: [permissionGuard],
         data: { permission: 'roles.permissions.manage', title: 'Permissões do perfil' },
         loadComponent: () => import('./features/roles/role-permissions-matrix/role-permissions-matrix.component').then((m) => m.RolePermissionsMatrixComponent)
       },

      // Clientes
      {
        path: 'clients',
        canActivate: [permissionGuard],
        data: { permission: 'clients.view', title: 'Clientes' },
        loadComponent: () => import('./features/clients/client-list/client-list.component').then((m) => m.ClientListComponent)
      },
      {
        path: 'clients/new',
        canActivate: [permissionGuard],
        data: { permission: 'clients.create', title: 'Novo cliente' },
        loadComponent: () => import('./features/clients/client-form/client-form.component').then((m) => m.ClientFormComponent)
      },
      {
        path: 'clients/:publicId/edit',
        canActivate: [permissionGuard],
        data: { permission: 'clients.update', title: 'Editar cliente' },
        loadComponent: () => import('./features/clients/client-form/client-form.component').then((m) => m.ClientFormComponent)
      },
      {
        path: 'clients/:publicId',
        canActivate: [permissionGuard],
        data: { permission: 'clients.view', title: 'Cliente' },
        loadComponent: () => import('./features/clients/client-detail/client-detail.component').then((m) => m.ClientDetailComponent)
      },

      // Casos jurídicos
      {
        path: 'cases',
        canActivate: [permissionGuard],
        data: { permission: 'cases.view', title: 'Processos' },
        loadComponent: () => import('./features/legal-cases/legal-case-list/legal-case-list.component').then((m) => m.LegalCaseListComponent)
      },
      {
        path: 'cases/new',
        canActivate: [permissionGuard],
        data: { permission: 'cases.create', title: 'Novo processo' },
        loadComponent: () => import('./features/legal-cases/legal-case-form/legal-case-form.component').then((m) => m.LegalCaseFormComponent)
      },
      {
        path: 'cases/:publicId',
        canActivate: [permissionGuard],
        data: { permission: 'cases.view', title: 'Processo' },
        loadComponent: () => import('./features/legal-cases/legal-case-detail/legal-case-detail.component').then((m) => m.LegalCaseDetailComponent)
      },
      {
        path: 'cases/:publicId/edit',
        canActivate: [permissionGuard],
        data: { permission: 'cases.update', title: 'Editar processo' },
        loadComponent: () => import('./features/legal-cases/legal-case-form/legal-case-form.component').then((m) => m.LegalCaseFormComponent)
      },

      // Tarefas
      {
        path: 'tasks',
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [permissionGuard],
            data: { permission: 'tasks.view', title: 'Tarefas' },
            loadComponent: () => import('./features/tasks/pages/task-board/task-board.component').then((m) => m.TaskBoardComponent)
          },
          {
            path: 'new',
            canActivate: [permissionGuard],
            data: { permission: 'tasks.create', title: 'Nova tarefa' },
            loadComponent: () => import('./features/tasks/pages/task-form/task-form.component').then((m) => m.TaskFormComponent)
          },
          {
            path: 'settings',
            canActivate: [permissionGuard],
            data: { permission: 'tasks.categories.manage', title: 'Categorias e status' },
            loadComponent: () => import('./features/tasks/pages/task-admin/task-admin.component').then((m) => m.TaskAdminComponent)
          },
          {
            path: ':publicId',
            canActivate: [permissionGuard],
            data: { permission: 'tasks.view', title: 'Detalhes da tarefa' },
            loadComponent: () => import('./features/tasks/pages/task-detail/task-detail.component').then((m) => m.TaskDetailComponent)
          },
          {
            path: ':publicId/edit',
            canActivate: [permissionGuard],
            data: { permission: 'tasks.view', title: 'Editar tarefa' },
            loadComponent: () => import('./features/tasks/pages/task-form/task-form.component').then((m) => m.TaskFormComponent)
          }
        ]
      },

      // Financeiro
      {
        path: 'financial',
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [permissionGuard],
            data: { permission: 'finance.overview.view', title: 'Visão geral financeira' },
            loadComponent: () => import('./features/financial/pages/financial-overview/financial-overview.component').then((m) => m.FinancialOverviewPageComponent)
          },
          {
            path: 'payments',
            canActivate: [permissionGuard],
            data: { permission: 'finance.view', title: 'Cobranças' },
            loadComponent: () => import('./features/financial/pages/payment-list/payment-list.component').then((m) => m.PaymentListComponent)
          },
          {
            path: 'payments/create',
            canActivate: [permissionGuard],
            data: { permission: 'finance.create', title: 'Nova cobrança' },
            loadComponent: () => import('./features/financial/pages/payment-create/payment-create.component').then((m) => m.PaymentCreateComponent)
          },
          {
            path: 'payments/:publicId',
            canActivate: [permissionGuard],
            data: { permission: 'finance.view', title: 'Cobrança' },
            loadComponent: () => import('./features/financial/pages/payment-detail/payment-detail.component').then((m) => m.PaymentDetailComponent)
          },
          {
            path: 'subscriptions',
            canActivate: [permissionGuard],
            data: { permission: 'finance.subscriptions.view', title: 'Assinaturas' },
            loadComponent: () => import('./features/financial/pages/subscription-list/subscription-list.component').then((m) => m.SubscriptionListComponent)
          },
          {
            path: 'subscriptions/create',
            canActivate: [permissionGuard],
            data: { permission: 'finance.subscriptions.create', title: 'Nova assinatura' },
            loadComponent: () => import('./features/financial/pages/subscription-create/subscription-create.component').then((m) => m.SubscriptionCreateComponent)
          },
          {
            path: 'subscriptions/:publicId',
            canActivate: [permissionGuard],
            data: { permission: 'finance.subscriptions.view', title: 'Assinatura' },
            loadComponent: () => import('./features/financial/pages/subscription-detail/subscription-detail.component').then((m) => m.SubscriptionDetailComponent)
          },
          {
            path: 'reports/espelho-nfs',
            canActivate: [permissionGuard],
            data: { permission: 'finance.reports.view', title: 'Relatório Contábil' },
            loadComponent: () => import('./features/financial/pages/accounting-report/accounting-report.component').then((m) => m.AccountingReportComponent)
          }
        ]
      },

      // Escritório — Custos e Despesas
      {
        path: 'office/expenses',
        canActivate: [permissionGuard],
        data: { permission: 'office_expenses.view', title: 'Lançamentos' },
        loadComponent: () => import('./features/office/pages/expense-list/expense-list.component').then((m) => m.ExpenseListComponent)
      },
      {
        path: 'office/expenses/categories',
        canActivate: [permissionGuard],
        data: { permission: 'office_expense_categories.manage', title: 'Categorias de despesas' },
        loadComponent: () =>
          import('./features/office/pages/expense-category-admin/expense-category-admin.component').then(
            (m) => m.ExpenseCategoryAdminComponent
          )
      },

      // Configurações de integrações
      {
        path: 'settings/integrations/asaas',
        canActivate: [permissionGuard],
        data: { permission: 'integrations.view', title: 'Integração Asaas' },
        loadComponent: () => import('./features/settings/integrations/asaas-settings/asaas-settings.component').then((m) => m.AsaasSettingsComponent)
      },
      {
        path: 'settings/integrations/google-calendar',
        canActivate: [permissionGuard],
        data: { permission: 'integrations.google_calendar.view', title: 'Google Agenda' },
        loadComponent: () => import('./features/settings/integrations/google-calendar-settings/google-calendar-settings.component').then((m) => m.GoogleCalendarSettingsComponent)
      },

      // Webhooks
      {
        path: 'settings/webhooks',
        canActivate: [permissionGuard],
        data: { permission: 'webhooks.view', title: 'Webhooks' },
        loadComponent: () => import('./features/settings/webhooks/settings-webhooks.component').then((m) => m.SettingsWebhooksComponent)
      },
      {
        path: 'settings/webhooks/events',
        canActivate: [permissionGuard],
        data: { permission: 'webhooks.view', title: 'Eventos de webhook' },
        loadComponent: () => import('./features/settings/webhook-events/pages/webhook-events-list.component').then((m) => m.WebhookEventsListComponent)
      },
      {
        path: 'settings/webhooks/events/:publicId',
        canActivate: [permissionGuard],
        data: { permission: 'webhooks.view', title: 'Detalhe do evento' },
        loadComponent: () => import('./features/settings/webhook-events/pages/webhook-event-detail.component').then((m) => m.WebhookEventDetailComponent)
      },

      // Configurações gerais
      {
        path: 'settings',
        pathMatch: 'full',
        canActivate: [permissionGuard],
        data: { permission: 'settings.view', title: 'Configurações' },
        loadComponent: () => import('./features/settings/settings-home/settings-home.component').then((m) => m.SettingsHomeComponent)
      },
      {
        path: 'settings/office-profile',
        canActivate: [permissionGuard],
        data: { permission: 'office_profile.view', title: 'Perfil do escritório' },
        loadComponent: () => import('./features/settings/office-profile/office-profile.component').then((m) => m.OfficeProfileComponent)
      },
      {
        path: 'settings/email-deliveries',
        canActivate: [permissionGuard],
        data: { permission: 'email_deliveries.view', title: 'Entregas de e-mail' },
        loadComponent: () =>
          import('./features/settings/email-deliveries/email-deliveries.component').then((m) => m.EmailDeliveriesComponent)
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'login'
  }
];
